import { SeverityLevel } from "@microsoft/applicationinsights-web";
import { appInsights } from "@/appInsights";

/**
 * Every level takes the same context. `operation` is the Kusto grouping key: a
 * stable lowerCamelCase name of the function or user action. Add the upstream
 * ids you have (documentId, workspaceId, …), never tokens, payloads or raw PII.
 */
export interface LogContext {
  operation: string;
  /**
   * The caught value, of any type. `error()` sends it as the exception;
   * `warn()`/`info()` attach it as text.
   */
  error?: unknown;
  [key: string]: unknown;
}

const isDev = import.meta.env.DEV;

/**
 * Email addresses reach logs inside error text (the API answers an invite with
 * "User already exists: <emails>"), and App Insights must not hold them, so
 * every string the logger sends is masked. Names cannot be caught this way:
 * log ids instead of file, folder, workspace or person names.
 */
const EMAIL_ADDRESS =
  /[A-Za-z0-9æøåÆØÅ._%+-]+@[A-Za-z0-9æøåÆØÅ-]+(?:\.[A-Za-z0-9æøåÆØÅ-]+)+/g;
const maskEmails = (text: string): string =>
  text.replace(EMAIL_ADDRESS, "[email]");

/**
 * App Insights `JSON.stringify`s object properties, and an `Error` has no
 * enumerable fields, so it would arrive as `{}`. Send it as text instead.
 */
const toText = (value: unknown): string => {
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  if (typeof value === "string") return value;
  try {
    const json = JSON.stringify(value);
    if (json !== undefined && json !== "{}") return json;
  } catch {
    // Circular or BigInt: fall back to String() below.
  }
  return String(value);
};
const describe = (value: unknown): string => maskEmails(toText(value));

const toProperties = (
  context: Record<string, unknown>
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(context).map(([key, value]) => [
      key,
      value instanceof Error
        ? describe(value)
        : typeof value === "string"
          ? maskEmails(value)
          : value,
    ])
  );

/** Only an `Error` carries a stack; keep what an error-like value has. */
const toException = (message: string, value: unknown): Error => {
  if (value instanceof Error) return value;
  if (
    typeof value === "object" &&
    value !== null &&
    "message" in value &&
    typeof value.message === "string"
  ) {
    const exception = new Error(value.message);
    if ("name" in value && typeof value.name === "string") {
      exception.name = value.name;
    }
    if ("stack" in value && typeof value.stack === "string") {
      exception.stack = value.stack;
    }
    return exception;
  }
  return new Error(message);
};

/** The exception's message and stack are sent as they are, so mask them too. */
const maskException = (exception: Error): Error => {
  const message = maskEmails(exception.message);
  const stack = exception.stack && maskEmails(exception.stack);
  if (message === exception.message && stack === exception.stack) {
    return exception;
  }
  const masked = new Error(message);
  masked.name = exception.name;
  masked.stack = stack;
  return masked;
};

const trace = (
  severityLevel: SeverityLevel,
  message: string,
  { error, ...context }: LogContext
) => {
  appInsights.trackTrace({
    message: maskEmails(message),
    severityLevel,
    properties: {
      ...toProperties(context),
      ...(error === undefined ? {} : { error: describe(error) }),
    },
  });
};

export const logger = {
  error: (message: string, context: LogContext) => {
    if (isDev) {
      console.error(message, context);
    }

    const { error, ...rest } = context;
    appInsights.trackException({
      exception: maskException(toException(message, error)),
      properties: {
        message: maskEmails(message),
        ...toProperties(rest),
        // A non-Error value never reaches the exception intact; keep it here.
        ...(error === undefined || error instanceof Error
          ? {}
          : { thrown: describe(error) }),
      },
    });
  },

  warn: (message: string, context: LogContext) => {
    if (isDev) {
      console.warn(message, context);
    }
    trace(SeverityLevel.Warning, message, context);
  },

  info: (message: string, context: LogContext) => {
    if (isDev) {
      console.info(message, context);
    }
    trace(SeverityLevel.Information, message, context);
  },
};
